import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LogOut } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useSite } from "@/lib/site";
import { useSignInModal } from "@/components/SignInModal";

const DEFAULT_LINKS = [
  { id: "nav-browse", label: "Discover", href: "#shift" },
  { id: "nav-mission", label: "Products", href: "#campaign" },
  { id: "nav-projects", label: "Projects", href: "#problems" },
  { id: "nav-collaborate", label: "Collaborate", href: "#join" },
  { id: "nav-cta", label: "Book a consultation", href: "#contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { openSignIn, SignInModal } = useSignInModal();
  const { links: cmsLinks } = useSite();
  const allLinks = cmsLinks && cmsLinks.length > 0 ? cmsLinks : DEFAULT_LINKS;
  const links = allLinks.filter((l) => l.id !== "nav-cta");
  const cta = allLinks.find((l) => l.id === "nav-cta") || DEFAULT_LINKS[4];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (href?.startsWith("#")) {
      document.getElementById(href.slice(1))?.scrollIntoView({ behavior: "smooth" });
    } else if (href) {
      window.location.href = href;
    }
  };

  return (
    <motion.header
      data-testid="main-nav"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-40 transition-[background,border] duration-300 ${
        scrolled ? "bg-[#1F3864]/80 backdrop-blur-xl border-b border-white/10" : "border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("#top")}
          className="flex items-center"
          aria-label="Rada Collab home"
        >
          <img src="/rada-logo-white.png" alt="Rada Collab" className="h-5 md:h-7 w-auto" />
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.href)}
              className="text-sm font-bold uppercase tracking-[0.15em] text-[#FFFFFF] hover:text-[#D7E85C] transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <div className="flex items-center gap-3" data-testid="nav-user">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full object-cover border border-[#D7E85C]" />
              ) : (
                <span className="grid place-items-center h-8 w-8 rounded-full bg-[#D7E85C] text-[#1F3864] font-bold text-sm">
                  {(user.name || "U").charAt(0)}
                </span>
              )}
              <a href="/account" data-testid="nav-account-link" className="text-sm font-bold text-[#EBE6E0] max-w-[120px] truncate hover:text-[#D7E85C]">{user.name}</a>
              <button
                data-testid="nav-logout"
                onClick={logout}
                aria-label="Log out"
                className="grid place-items-center h-9 w-9 rounded-full bg-white/10 text-[#EBE6E0] hover:bg-[#FF8473] hover:text-[#1F3864] transition-colors"
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <button
              data-testid="nav-signin"
              onClick={openSignIn}
              className="inline-flex items-center text-sm font-bold uppercase tracking-wide text-[#EBE6E0] hover:text-[#D7E85C] transition-colors"
            >
              Sign in
            </button>
          )}
          <button
            data-testid="nav-cta"
            onClick={() => go(cta.href)}
            className="inline-flex items-center bg-[#D7E85C] text-[#1F3864] px-5 py-2.5 rounded-full text-sm font-bold uppercase tracking-wide hover:bg-[#EBE6E0] active:scale-95 transition-[background,transform] duration-200"
          >
            {cta.label}
          </button>
        </div>

        <button
          data-testid="nav-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className="md:hidden font-bold uppercase tracking-widest text-sm text-[#EBE6E0]"
        >
          {open ? "Close" : "Menu"}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#16294B] border-t border-white/10"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-${l.id}`}
                  onClick={() => go(l.href)}
                  className="text-left font-display font-bold text-2xl text-[#EBE6E0]"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-cta"
                onClick={() => go(cta.href)}
                className="mt-2 bg-[#D7E85C] text-[#1F3864] px-5 py-3 rounded-full font-bold uppercase text-sm text-left"
              >
                {cta.label}
              </button>
              {user ? (
                <>
                  <a
                    href="/account"
                    data-testid="nav-mobile-account"
                    onClick={() => setOpen(false)}
                    className="text-left font-bold uppercase text-sm text-[#D7E85C]"
                  >
                    My account ({user.name})
                  </a>
                  <button
                    data-testid="nav-mobile-logout"
                    onClick={() => { setOpen(false); logout(); }}
                    className="text-left font-bold uppercase text-sm text-[#FF8473]"
                  >
                    Log out
                  </button>
                </>
              ) : (
                <button
                  data-testid="nav-mobile-signin"
                  onClick={() => { setOpen(false); openSignIn(); }}
                  className="text-left font-bold uppercase text-sm text-[#EBE6E0]"
                >
                  Sign in
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {SignInModal}
    </motion.header>
  );
};
